<template>
  <div class="teacher-page d-flex">
    <aside class="side-nav" :class="{ collapsed: sidebarCollapsed }">
      <div class="side-head">
        <h4 class="mb-0" v-show="!sidebarCollapsed">教师端</h4>
        <button class="collapse-btn" @click="toggleSidebar">{{ sidebarCollapsed ? '>>' : '<<' }}</button>
      </div>
      <button class="sub-btn" @click="goQuestionManage"><span class="nav-text">题库管理</span></button>
      <button class="sub-btn" @click="goPaperManage"><span class="nav-text">试卷管理</span></button>
      <button class="sub-btn" @click="goExamManage"><span class="nav-text">考试管理</span></button>
      <button class="sub-btn" @click="goGradingManage"><span class="nav-text">阅卷管理</span></button>
      <button class="sub-btn" @click="goExamRecordManage"><span class="nav-text">考生试卷管理</span></button>
      <button class="sub-btn active"><span class="nav-text">监考系统</span></button>
      <button class="sub-btn" @click="goStudentUsers"><span class="nav-text">学生用户管理</span></button>
      <div class="mt-auto">
        <button class="btn btn-sm btn-light w-100 mb-2" @click="logout">退出登录</button>
      </div>
    </aside>

    <main class="content-area">
      <header class="topbar">
        <h2>监考系统</h2>
        <div class="actions">
          <span class="text-muted small">当前账号：{{ currentUser.username || '教师' }}</span>
        </div>
      </header>

      <section v-if="phase === 'pick'" class="card-box">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">选择考场</h4>
          <button class="btn btn-sm btn-light" type="button" @click="loadExamList">刷新列表</button>
        </div>
        <p class="text-muted small mb-3">
          请选择一场考试进入实时监考。学员在考试中且摄像头监控开启时，画面约每 5 秒尝试上报（服务端节流约 4 秒），教师端约每 3 秒自动刷新；可在考场中向全体学员推送居中通知，通知期间不会记入切屏作弊。
        </p>
        <div class="result-shell">
          <table class="table table-striped align-middle modern-table mb-0">
            <thead>
              <tr>
                <th>考试名称</th>
                <th>状态</th>
                <th>开考时间</th>
                <th>结束时间</th>
                <th>参考人数</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in examRows" :key="row.exam && row.exam.id">
                <td>{{ row.exam && row.exam.examName ? row.exam.examName : '—' }}</td>
                <td>
                  <span class="badge" :class="examStatusBadge(row.exam)">{{ examStatusText(row.exam) }}</span>
                </td>
                <td>{{ formatDt(row.exam && row.exam.startTime) }}</td>
                <td>{{ formatDt(row.exam && row.exam.endTime) }}</td>
                <td>{{ row.participantCount != null ? row.participantCount : 0 }}</td>
                <td>
                  <button class="btn btn-sm btn-primary" type="button" @click="enterProctor(row.exam)">进入监考</button>
                </td>
              </tr>
              <tr v-if="examRows.length === 0">
                <td colspan="6" class="text-muted">暂无考试，请先在「考试管理」中创建并发布。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="phase === 'live'" class="card-box broadcast-card">
        <h5 class="mb-2">向本场考生推送通知</h5>
        <p class="small text-muted mb-2">
          学员答题界面将居中弹出通知，默认展示约 1 分钟后自动关闭，也可随时点「关闭」；弹出与关闭过程<strong>不会</strong>记为切屏或行为作弊。
        </p>
        <textarea
          v-model="broadcastDraft"
          class="form-control mb-2"
          rows="3"
          maxlength="4000"
          placeholder="填写要广播给本场全部学员的文字（如：剩余时间提醒、纪律说明等）"
        />
        <div class="d-flex flex-wrap align-items-center gap-2">
          <button class="btn btn-primary btn-sm" type="button" :disabled="broadcastSending" @click="publishBroadcast">
            发送给本场全部学员
          </button>
          <span v-if="broadcastOkMsg" class="small text-success">{{ broadcastOkMsg }}</span>
        </div>
      </section>

      <section v-if="phase === 'live'" class="card-box raise-hand-card">
        <h5 class="mb-2">学员举手（待响应）</h5>
        <p class="small text-muted mb-2">
          学员在考试中点击「举手」后，将出现在下列列表。点击「响应」将自动打开放大监考画面并尝试建立与其的实时语音；也可在弹窗内与该学员单独打字沟通。
        </p>
        <div v-if="raiseHandList.length === 0" class="small text-muted">暂无待处理举手</div>
        <ul v-else class="list-unstyled mb-0">
          <li
            v-for="h in raiseHandList"
            :key="'rh-' + h.recordId"
            class="d-flex flex-wrap align-items-center gap-2 py-1 border-bottom"
          >
            <strong>{{ h.username || '学员' }}</strong>
            <span class="small text-muted">记录 #{{ h.recordId }}</span>
            <button type="button" class="btn btn-sm btn-primary ms-auto" @click="respondRaiseHand(h)">响应</button>
          </li>
        </ul>
      </section>

      <section v-if="phase === 'live'" class="card-box proctor-live-wrap">
        <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
          <button class="btn btn-outline-secondary btn-sm" type="button" @click="leaveProctor">← 返回选考场</button>
          <h4 class="mb-0 ms-1">「{{ activeExamName }}」实时画面</h4>
          <span class="text-muted small ms-auto">每页最多 {{ livePageSize }} 人 · 共 {{ liveTotal }} 人</span>
          <button class="btn btn-sm btn-light" type="button" @click="fetchLive(false)">立即刷新</button>
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2 mb-2 proctor-pagination">
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            :disabled="livePage <= 0"
            @click="goLivePage(livePage - 1)"
          >
            上一页
          </button>
          <span class="small text-muted">
            第 {{ liveTotalPages ? livePage + 1 : 0 }} / {{ liveTotalPages || 0 }} 页
          </span>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            :disabled="livePage >= liveTotalPages - 1"
            @click="goLivePage(livePage + 1)"
          >
            下一页
          </button>
          <span class="text-muted small">自动刷新 {{ pollSeconds }} 秒 · {{ lastFetchText }}</span>
          <button class="btn btn-sm btn-primary ms-auto" type="button" @click="openProctorWallNewWindow">
            新窗口打开监控墙（本页）
          </button>
        </div>
        <p class="small text-muted mb-2">
          画面为紧凑网格（单页最多 50 路）。多显示器可同时打开多个窗口，将地址中
          <code class="small">page=0</code>、<code class="small">page=1</code>…改为不同页码即可同步监控更多学员。示例：
          <code class="small user-select-all">{{ proctorWallLinkHint() }}</code>
        </p>
        <p class="small text-muted mb-2">
          提示：双击某位学员卡片可放大监考画面，并尝试与该学员建立实时语音（需浏览器允许麦克风；若网络限制 UDP 可能失败）。
        </p>
        <div class="proctor-grid">
          <div
            v-for="s in liveStudents"
            :key="s.recordId"
            class="proctor-card proctor-card-click"
            @dblclick.prevent="openEnlargeStudent(s)"
          >
            <div class="proctor-card-head">
              <strong>{{ s.username || '学员' }}</strong>
              <span class="badge bg-secondary">{{ recordStatusText(s.status) }}</span>
            </div>
            <div class="proctor-thumb">
              <img v-if="previewPhotoSrc(s)" :src="previewPhotoSrc(s)" alt="monitor" class="proctor-img" />
              <div v-else class="proctor-placeholder">暂无画面<br /><span class="small">等待学员端上报</span></div>
            </div>
            <div class="proctor-meta small text-muted">
              切屏 {{ s.screenCount != null ? s.screenCount : 0 }} 次 ·
              画面时间 {{ monitorTimeText(s) || '—' }}
            </div>
          </div>
          <div v-if="liveStudents.length === 0" class="text-muted">本场暂无参考学员记录。</div>
        </div>
      </section>

      <div v-if="enlargeModalOpen" class="enlarge-mask" @click.self="closeEnlargeStudent">
        <div class="enlarge-panel" @click.stop>
          <div class="enlarge-head">
            <div>
              <h4 class="mb-0">放大监考 · {{ voiceFocusStudent && (voiceFocusStudent.username || '学员') }}</h4>
              <div class="small text-muted">画面随列表自动刷新 · 话筒打开时教师端才向对方传送声音</div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeEnlargeStudent">关闭</button>
          </div>
          <div class="enlarge-body">
            <div class="enlarge-frame">
              <img v-if="enlargedPhotoSrc" :src="enlargedPhotoSrc" class="enlarge-img" alt="monitor" />
              <div v-else class="enlarge-placeholder">暂无画面</div>
            </div>
            <div class="enlarge-voice">
              <audio ref="teacherRemoteAudio" class="d-none" autoplay playsinline></audio>
              <div class="d-flex flex-wrap align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="teacherMicOn ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="toggleTeacherMic"
                >
                  {{ teacherMicOn ? '话筒开（点击关闭）' : '话筒关（点击对学员说话）' }}
                </button>
                <span v-if="teacherVoiceError" class="small text-danger">{{ teacherVoiceError }}</span>
                <span v-else-if="teacherVoiceConnecting" class="small text-muted">正在建立语音…</span>
                <span v-else class="small text-success">语音通道就绪（默认只听学员）</span>
              </div>
            </div>
            <div class="enlarge-dm mt-3">
              <div class="small fw-semibold mb-1">与该学员单独文字沟通</div>
              <div ref="proctorDmScroll" class="enlarge-dm-log small">
                <div v-for="m in proctorDmMessages" :key="'pdm-' + m.id" class="mb-1">
                  <span :class="m.fromRole === 'teacher' ? 'text-primary' : 'text-secondary'">
                    {{ m.fromRole === 'teacher' ? '我' : '学员' }}：
                  </span>
                  {{ m.content }}
                </div>
                <div v-if="proctorDmMessages.length === 0" class="text-muted">暂无消息（需已响应学员举手后方可收发）</div>
              </div>
              <div class="d-flex gap-2 mt-2">
                <input
                  v-model="proctorDmInput"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="输入文字…"
                  maxlength="2000"
                  @keydown.enter.prevent="sendProctorDm"
                />
                <button type="button" class="btn btn-sm btn-primary flex-shrink-0" :disabled="proctorDmSending" @click="sendProctorDm">
                  发送
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'TeacherProctorView',
  data() {
    return {
      sidebarCollapsed: false,
      currentUser: {},
      phase: 'pick',
      examRows: [],
      activeExamId: null,
      activeExamName: '',
      liveStudents: [],
      livePage: 0,
      livePageSize: 50,
      liveTotal: 0,
      liveTotalPages: 0,
      pollTimer: null,
      pollSeconds: 2,
      broadcastDraft: '',
      broadcastSending: false,
      broadcastOkMsg: '',
      lastFetchAt: null,

      enlargeModalOpen: false,
      voiceFocusStudent: null,
      teacherVoicePc: null,
      teacherVoiceLocalStream: null,
      teacherMicOn: false,
      teacherVoicePullTimer: null,
      teacherVoiceLastPullId: 0,
      teacherVoiceConnecting: false,
      teacherVoiceError: '',
      teacherStudentIcePending: [],

      raiseHandList: [],
      raiseHandPollTimer: null,
      proctorDmMessages: [],
      proctorDmInput: '',
      proctorDmLastId: 0,
      proctorDmPollTimer: null,
      proctorDmSending: false
    }
  },
  computed: {
    enlargedPhotoSrc() {
      if (!this.voiceFocusStudent) return ''
      const id = this.voiceFocusStudent.recordId
      const row = this.liveStudents.find((x) => x.recordId === id) || this.voiceFocusStudent
      return this.fullPhotoSrc(row) || this.previewPhotoSrc(row)
    },
    lastFetchText() {
      if (!this.lastFetchAt) return '—'
      try {
        return new Date(this.lastFetchAt).toLocaleTimeString()
      } catch (e) {
        return '—'
      }
    }
  },
  mounted() {
    try {
      this.currentUser = JSON.parse(localStorage.getItem('exam_user') || '{}')
    } catch (e) {
      this.currentUser = {}
    }
    this.loadExamList()
  },
  beforeUnmount() {
    this.stopPoll()
    this.stopRaiseHandPoll()
    this.closeEnlargeStudent()
  },
  methods: {
    authHeaders(extra = {}) {
      return { 'X-Role': 'teacher', ...extra }
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    logout() {
      this.stopPoll()
      localStorage.removeItem('exam_user')
      window.location.hash = '#/'
    },
    goQuestionManage() {
      window.location.hash = '#/teacher/questions'
    },
    goPaperManage() {
      localStorage.setItem('teacher_active_menu', 'paper')
      window.location.hash = '#/teacher/papers'
    },
    goExamManage() {
      localStorage.setItem('teacher_active_menu', 'exam')
      window.location.hash = '#/teacher/papers'
    },
    goGradingManage() {
      localStorage.setItem('teacher_active_menu', 'grading')
      window.location.hash = '#/teacher/papers'
    },
    goExamRecordManage() {
      window.location.hash = '#/teacher/exam-records'
    },
    goStudentUsers() {
      window.location.hash = '#/teacher/students'
    },
    loadExamList() {
      const params = new URLSearchParams({ page: '0', size: '200' })
      fetch(`/api/teacher/exam-manage/list?${params.toString()}`, { headers: this.authHeaders() })
        .then((r) => r.json())
        .then((res) => {
          const data = res.data
          this.examRows = Array.isArray(data) ? data : ((data && data.content) || [])
        })
        .catch(() => {
          this.examRows = []
        })
    },
    examStatusText(exam) {
      const s = String((exam && exam.status) || '').toUpperCase()
      if (s === 'NOT_START') return '未开始'
      if (s === 'ING') return '进行中'
      if (s === 'END') return '已结束'
      return (exam && exam.status) || '—'
    },
    examStatusBadge(exam) {
      const s = String((exam && exam.status) || '').toUpperCase()
      if (s === 'ING') return 'bg-success'
      if (s === 'END') return 'bg-secondary'
      if (s === 'NOT_START') return 'bg-warning text-dark'
      return 'bg-light text-dark'
    },
    formatDt(v) {
      if (v == null || v === '') return ''
      const str = String(v).replace(' ', 'T')
      const d = new Date(str)
      if (Number.isNaN(d.getTime())) return String(v)
      return d.toLocaleString()
    },
    monitorTimeText(s) {
      const ms = s && s.monitorTimeMs != null ? Number(s.monitorTimeMs) : 0
      if (Number.isFinite(ms) && ms > 0) {
        try {
          return new Date(ms).toLocaleTimeString()
        } catch (e) {
          void e
        }
      }
      return this.formatDt(s && s.monitorPhotoTime)
    },
    enterProctor(exam) {
      if (!exam || exam.id == null) return
      this.activeExamId = exam.id
      this.activeExamName = exam.examName || '考试'
      this.broadcastOkMsg = ''
      this.livePage = 0
      this.phase = 'live'
      this.fetchLive(false)
      this.startPoll()
      this.startRaiseHandPoll()
    },
    leaveProctor() {
      this.closeEnlargeStudent()
      this.stopPoll()
      this.stopRaiseHandPoll()
      this.phase = 'pick'
      this.activeExamId = null
      this.activeExamName = ''
      this.liveStudents = []
      this.livePage = 0
      this.liveTotal = 0
      this.liveTotalPages = 0
      this.lastFetchAt = null
      this.broadcastDraft = ''
      this.broadcastOkMsg = ''
      this.raiseHandList = []
    },
    startRaiseHandPoll() {
      this.stopRaiseHandPoll()
      if (this.activeExamId == null) return
      this.fetchRaiseHands()
      this.raiseHandPollTimer = setInterval(() => this.fetchRaiseHands(), 2000)
    },
    stopRaiseHandPoll() {
      if (this.raiseHandPollTimer) {
        clearInterval(this.raiseHandPollTimer)
        this.raiseHandPollTimer = null
      }
    },
    fetchRaiseHands() {
      if (this.activeExamId == null || this.phase !== 'live') return
      fetch(`/api/teacher/exam-hand/pending?examId=${this.activeExamId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200 && Array.isArray(res.data)) {
            this.raiseHandList = res.data
          }
        })
        .catch(() => {})
    },
    respondRaiseHand(h) {
      if (!h || h.recordId == null || this.activeExamId == null) return
      fetch('/api/teacher/exam-hand/respond', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ examId: this.activeExamId, recordId: h.recordId })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '响应失败')
            return null
          }
          return fetch(
            `/api/teacher/exam-monitor/exam/${this.activeExamId}/record/${h.recordId}`,
            { headers: this.authHeaders() }
          ).then((r2) => r2.json())
        })
        .then((res2) => {
          if (!res2) return
          if (res2.code !== 200 || !res2.data) {
            window.alert(res2.message || '加载学员画面失败')
            return
          }
          this.openEnlargeStudent(res2.data)
          this.fetchRaiseHands()
        })
        .catch(() => window.alert('网络错误'))
    },
    startProctorDmPoll() {
      this.stopProctorDmPoll()
      this.pollProctorDmOnce()
      this.proctorDmPollTimer = setInterval(() => this.pollProctorDmOnce(), 2000)
    },
    stopProctorDmPoll() {
      if (this.proctorDmPollTimer) {
        clearInterval(this.proctorDmPollTimer)
        this.proctorDmPollTimer = null
      }
    },
    pollProctorDmOnce() {
      if (!this.enlargeModalOpen || !this.voiceFocusStudent || this.activeExamId == null) return
      const rid = this.voiceFocusStudent.recordId
      fetch(
        `/api/teacher/exam-dm/messages?examId=${this.activeExamId}&recordId=${rid}&afterId=${this.proctorDmLastId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !Array.isArray(res.data)) return
          let maxId = this.proctorDmLastId
          for (const row of res.data) {
            const id = Number(row.id)
            if (!Number.isFinite(id)) continue
            if (this.proctorDmMessages.some((x) => x.id === id)) continue
            if (id > maxId) maxId = id
            this.proctorDmMessages.push({
              id,
              fromRole: String(row.fromRole || ''),
              content: String(row.content || '')
            })
          }
          this.proctorDmLastId = maxId
          this.$nextTick(() => {
            const el = this.$refs.proctorDmScroll
            if (el) el.scrollTop = el.scrollHeight
          })
        })
        .catch(() => {})
    },
    sendProctorDm() {
      const t = String(this.proctorDmInput || '').trim()
      if (!t || !this.voiceFocusStudent || this.activeExamId == null) return
      this.proctorDmSending = true
      fetch('/api/teacher/exam-dm/send', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExamId,
          recordId: this.voiceFocusStudent.recordId,
          content: t
        })
      })
        .then((r) => r.json())
        .then((res) => {
          this.proctorDmSending = false
          if (res.code !== 200) {
            window.alert(res.message || '发送失败')
            return
          }
          this.proctorDmInput = ''
          this.pollProctorDmOnce()
        })
        .catch(() => {
          this.proctorDmSending = false
          window.alert('网络错误')
        })
    },
    publishBroadcast() {
      const t = String(this.broadcastDraft || '').trim()
      if (!t) {
        window.alert('请输入通知内容')
        return
      }
      if (this.activeExamId == null) return
      this.broadcastSending = true
      this.broadcastOkMsg = ''
      fetch('/api/teacher/exam-broadcast/publish', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ examId: this.activeExamId, content: t })
      })
        .then((r) => r.json())
        .then((res) => {
          this.broadcastSending = false
          if (res.code === 200) {
            this.broadcastDraft = ''
            this.broadcastOkMsg = '已发送'
            setTimeout(() => {
              this.broadcastOkMsg = ''
            }, 4000)
            return
          }
          window.alert(res.message || '发送失败')
        })
        .catch(() => {
          this.broadcastSending = false
          window.alert('网络错误')
        })
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => this.fetchLive(true), this.pollSeconds * 1000)
    },
    stopPoll() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    fetchLive(silent = true) {
      if (this.activeExamId == null) return
      const params = new URLSearchParams({
        page: String(this.livePage),
        size: String(this.livePageSize)
      })
      fetch(`/api/teacher/exam-monitor/exam/${this.activeExamId}/live?${params.toString()}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200 && res.data) {
            const d = res.data
            this.liveStudents = d.content || []
            this.liveTotal = Number(d.total) || 0
            this.liveTotalPages = Number(d.totalPages) || 0
            if (typeof d.page === 'number') this.livePage = d.page
            this.lastFetchAt = Date.now()
            if (this.enlargeModalOpen && this.voiceFocusStudent) {
              const hit = this.liveStudents.find((s) => s.recordId === this.voiceFocusStudent.recordId)
              if (hit) {
                Object.assign(this.voiceFocusStudent, {
                  monitorPhotoPreview: hit.monitorPhotoPreview,
                  monitorPhotoTime: hit.monitorPhotoTime,
                  screenCount: hit.screenCount,
                  status: hit.status
                })
              }
            }
          } else if (!silent) {
            window.alert(res.message || '加载监考数据失败')
          }
        })
        .catch(() => {
          if (!silent) window.alert('网络错误，无法加载监考数据')
        })
    },
    goLivePage(p) {
      const max = Math.max(0, this.liveTotalPages - 1)
      const n = Math.min(Math.max(0, p), max)
      if (n === this.livePage) return
      this.livePage = n
      this.fetchLive(false)
    },
    openProctorWallNewWindow() {
      if (this.activeExamId == null) return
      const base = `${window.location.origin}${window.location.pathname}`
      const url = `${base}#/teacher/proctor-wall?examId=${this.activeExamId}&page=${this.livePage}`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    proctorWallLinkHint() {
      if (this.activeExamId == null) return ''
      const base = `${window.location.origin}${window.location.pathname}`
      return `${base}#/teacher/proctor-wall?examId=${this.activeExamId}&page=页码(从0起)`
    },
    imageFieldSrc(v) {
      const p = v
      if (!p || !String(p).trim()) return ''
      const t = String(p).trim()
      if (t.startsWith('data:image')) return t
      return `data:image/jpeg;base64,${t}`
    },
    previewPhotoSrc(s) {
      return this.imageFieldSrc(s && (s.monitorPhotoPreview || s.monitorPhoto))
    },
    fullPhotoSrc(s) {
      return this.imageFieldSrc(s && s.monitorPhoto)
    },
    photoSrc(s) {
      const p = s && s.monitorPhoto
      if (!p || !String(p).trim()) return ''
      const t = String(p).trim()
      if (t.startsWith('data:image')) return t
      return `data:image/jpeg;base64,${t}`
    },
    recordStatusText(st) {
      const s = String(st || '').toUpperCase()
      if (s === 'ASSIGNED') return '已分配'
      if (s === 'EXAMING') return '作答中'
      if (s === 'COMMIT') return '已交卷'
      if (s === 'FORCE_END') return '强制交卷'
      if (s === 'GRADED') return '已阅卷'
      return st || '—'
    },
    iceServers() {
      return [{ urls: 'stun:stun.l.google.com:19302' }]
    },
    async openEnlargeStudent(s) {
      if (!s || s.recordId == null || this.activeExamId == null) return
      await this.closeEnlargeStudent()
      this.voiceFocusStudent = { ...s }
      this.enlargeModalOpen = true
      try {
        const res = await fetch(`/api/teacher/exam-monitor/exam/${this.activeExamId}/record/${s.recordId}`, {
          headers: this.authHeaders()
        }).then((r) => r.json())
        if (res && res.code === 200 && res.data) {
          this.voiceFocusStudent = { ...res.data }
        }
      } catch (e) {
        void e
      }
      this.teacherVoiceError = ''
      this.teacherVoiceConnecting = true
      this.teacherMicOn = false
      this.teacherVoiceLastPullId = 0
      this.teacherStudentIcePending = []
      this.proctorDmMessages = []
      this.proctorDmLastId = 0
      this.proctorDmInput = ''
      this.stopProctorDmPoll()
      await this.clearVoiceRoom()
      await this.$nextTick()
      this.initTeacherWebRtc().catch((e) => {
        this.teacherVoiceConnecting = false
        this.teacherVoiceError = (e && e.message) || '语音初始化失败'
      })
      this.startProctorDmPoll()
    },
    async clearVoiceRoom() {
      if (this.activeExamId == null || !this.voiceFocusStudent) return
      try {
        await fetch('/api/teacher/exam-voice/clear-room', {
          method: 'POST',
          headers: this.authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            examId: this.activeExamId,
            recordId: this.voiceFocusStudent.recordId
          })
        })
      } catch (e) {
        void e
      }
    },
    async pushTeacherVoiceSignal(signalType, payloadStr) {
      if (this.activeExamId == null || !this.voiceFocusStudent) return
      await fetch('/api/teacher/exam-voice/signal', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExamId,
          recordId: this.voiceFocusStudent.recordId,
          signalType,
          payload: payloadStr
        })
      })
    },
    async initTeacherWebRtc() {
      if (!window.RTCPeerConnection || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.teacherVoiceConnecting = false
        this.teacherVoiceError = '当前浏览器不支持 WebRTC 麦克风'
        return
      }
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      } catch (e) {
        this.teacherVoiceConnecting = false
        this.teacherVoiceError = '无法使用麦克风，请允许权限后关闭本窗口重试'
        throw e
      }
      this.teacherVoiceLocalStream = stream
      const pc = new RTCPeerConnection({ iceServers: this.iceServers })
      this.teacherVoicePc = pc
      pc.ontrack = (ev) => {
        const el = this.$refs.teacherRemoteAudio
        if (el && ev.streams && ev.streams[0]) {
          el.srcObject = ev.streams[0]
        }
      }
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          this.teacherVoiceError = '语音连接失败，可关闭后重试'
        }
      }
      stream.getAudioTracks().forEach((t) => {
        t.enabled = false
        pc.addTrack(t, stream)
      })
      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          this.pushTeacherVoiceSignal('candidate', JSON.stringify(ev.candidate.toJSON())).catch(() => {})
        }
      }
      const offer = await pc.createOffer({ offerToReceiveAudio: true })
      await pc.setLocalDescription(offer)
      await this.pushTeacherVoiceSignal('offer', JSON.stringify({ type: 'offer', sdp: offer.sdp }))
      this.teacherVoiceConnecting = false
      this.startTeacherVoicePull()
    },
    startTeacherVoicePull() {
      this.stopTeacherVoicePull()
      this.teacherVoicePullTimer = setInterval(() => this.pullTeacherVoiceOnce(), 2000)
    },
    stopTeacherVoicePull() {
      if (this.teacherVoicePullTimer) {
        clearInterval(this.teacherVoicePullTimer)
        this.teacherVoicePullTimer = null
      }
    },
    pullTeacherVoiceOnce() {
      if (!this.enlargeModalOpen || !this.voiceFocusStudent || this.activeExamId == null) return
      const rid = this.voiceFocusStudent.recordId
      fetch(
        `/api/teacher/exam-voice/signal?examId=${this.activeExamId}&recordId=${rid}&afterId=${this.teacherVoiceLastPullId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !res.data) return
          const rows = res.data
          let maxId = this.teacherVoiceLastPullId
          for (const row of rows) {
            const id = Number(row.id)
            if (Number.isFinite(id) && id > maxId) maxId = id
            this.applyTeacherSideStudentSignal(row)
          }
          this.teacherVoiceLastPullId = maxId
        })
        .catch(() => {})
    },
    async applyTeacherSideStudentSignal(row) {
      const pc = this.teacherVoicePc
      if (!pc || !row) return
      const t = String(row.signalType || '')
      const raw = String(row.payload || '{}')
      try {
        if (t === 'answer') {
          const j = JSON.parse(raw)
          const sdp = j && j.sdp ? String(j.sdp) : ''
          if (!sdp) return
          await pc.setRemoteDescription({ type: 'answer', sdp })
          const pend = this.teacherStudentIcePending.splice(0)
          for (const c of pend) {
            try {
              await pc.addIceCandidate(c)
            } catch (e) {
              void e
            }
          }
        } else if (t === 'candidate') {
          const j = JSON.parse(raw)
          const cand = new RTCIceCandidate(j)
          if (!pc.remoteDescription) {
            this.teacherStudentIcePending.push(cand)
            return
          }
          await pc.addIceCandidate(cand)
        } else if (t === 'hangup') {
          this.teacherVoiceError = '学员已结束语音'
        }
      } catch (e) {
        void e
      }
    },
    toggleTeacherMic() {
      this.teacherMicOn = !this.teacherMicOn
      if (this.teacherVoiceLocalStream) {
        this.teacherVoiceLocalStream.getAudioTracks().forEach((t) => {
          t.enabled = this.teacherMicOn
        })
      }
    },
    async closeEnlargeStudent() {
      const hangExamId = this.activeExamId
      const hangRecordId = this.voiceFocusStudent && this.voiceFocusStudent.recordId
      const hadSession = hangExamId != null && hangRecordId != null
      this.stopProctorDmPoll()
      this.proctorDmMessages = []
      this.proctorDmLastId = 0
      this.proctorDmInput = ''
      this.stopTeacherVoicePull()
      if (this.teacherVoicePc) {
        try {
          this.teacherVoicePc.close()
        } catch (e) {
          void e
        }
        this.teacherVoicePc = null
      }
      if (this.teacherVoiceLocalStream) {
        this.teacherVoiceLocalStream.getTracks().forEach((t) => t.stop())
        this.teacherVoiceLocalStream = null
      }
      const audio = this.$refs.teacherRemoteAudio
      if (audio) audio.srcObject = null
      if (hadSession) {
        try {
          await fetch('/api/teacher/exam-hand/end-session', {
            method: 'POST',
            headers: this.authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ examId: hangExamId, recordId: hangRecordId })
          })
        } catch (e) {
          void e
        }
        try {
          await fetch('/api/teacher/exam-voice/signal', {
            method: 'POST',
            headers: this.authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              examId: hangExamId,
              recordId: hangRecordId,
              signalType: 'hangup',
              payload: '{}'
            })
          })
        } catch (e) {
          void e
        }
      }
      this.enlargeModalOpen = false
      this.voiceFocusStudent = null
      this.teacherMicOn = false
      this.teacherVoiceConnecting = false
      this.teacherVoiceError = ''
      this.teacherVoiceLastPullId = 0
      this.teacherStudentIcePending = []
    }
  }
}
</script>

<style scoped>
/* 与阅卷工作台（TeacherGradingWorkspaceView）侧栏、顶栏、卡片风格一致 */
.teacher-page {
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(160deg, #f7faff, #eef4ff);
  color: #1f2a44;
}
.side-nav {
  width: 240px;
  min-height: calc(100vh - 40px);
  margin-right: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(63, 108, 199, 0.1);
}
.side-nav.collapsed {
  width: 76px;
}
.side-nav.collapsed .nav-text {
  display: none;
}
.side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.collapse-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #d8e6ff;
  background: #f6f9ff;
  color: #335896;
  font-size: 12px;
  padding: 0;
  line-height: 1;
}
.sub-btn {
  border: 1px solid #d8e6ff;
  border-radius: 10px;
  padding: 10px 12px;
  color: #335896;
  background: #f6f9ff;
  text-align: left;
  transition: all 0.2s ease;
}
.sub-btn:hover {
  background: #eef5ff;
  border-color: #bfd6ff;
  transform: translateY(-1px);
}
.sub-btn.active {
  background: linear-gradient(120deg, #2f6feb, #5ca2ff);
  color: #fff;
  border-color: transparent;
}
.content-area {
  flex: 1;
  min-width: 0;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.card-box {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.result-shell {
  border: 1px solid rgba(145, 180, 240, 0.35);
  border-radius: 12px;
  padding: 8px;
  overflow: auto;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
}
.modern-table {
  font-size: 14px;
}
.proctor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px;
}
.proctor-card {
  border: 1px solid #d8e6ff;
  border-radius: 10px;
  padding: 8px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.9), rgba(246, 249, 255, 0.95));
  box-shadow: 0 4px 12px rgba(48, 96, 192, 0.05);
}
.proctor-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 12px;
}
.proctor-card-head strong {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.proctor-thumb {
  position: relative;
  border-radius: 8px;
  border: 1px solid rgba(145, 180, 240, 0.35);
  overflow: hidden;
  background: #0f172a;
  aspect-ratio: 4 / 3;
}
.proctor-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.proctor-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 11px;
  text-align: center;
  padding: 8px;
}
.proctor-meta {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.35;
}
.proctor-pagination .badge,
.proctor-card .badge {
  font-size: 10px;
  padding: 0.2em 0.45em;
}
.proctor-card-click {
  cursor: pointer;
}
.proctor-card-click:active {
  transform: scale(0.99);
}
.enlarge-mask {
  position: fixed;
  inset: 0;
  z-index: 8000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.enlarge-panel {
  width: min(960px, 100%);
  max-height: min(92vh, 900px);
  background: #fff;
  border-radius: 16px;
  border: 1px solid #cfe0ff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.enlarge-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e8eef8;
}
.enlarge-body {
  padding: 12px 16px 16px;
  overflow: auto;
}
.enlarge-frame {
  border-radius: 12px;
  overflow: hidden;
  background: #0f172a;
  aspect-ratio: 16 / 10;
  max-height: 56vh;
}
.enlarge-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.enlarge-placeholder {
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}
.enlarge-voice {
  margin-top: 12px;
}
.enlarge-dm-log {
  max-height: 160px;
  overflow-y: auto;
  padding: 8px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
@media (max-width: 992px) {
  .teacher-page {
    flex-direction: column;
  }
  .side-nav {
    width: 100%;
    min-height: auto;
    margin-right: 0;
    margin-bottom: 12px;
  }
}
</style>
